/**
 * drag-move — drag-to-move bounding boxes on the PDF preview pane.
 *
 * Wires pointer events on existing `.editor-overlay-bbox` divs so the user
 * can reposition struct elements directly. Multi-select drag follows: if
 * the dragged box is in the current selection AND |selection| ≥ 2, every
 * selected box moves with the same delta. Otherwise the drag-source
 * becomes the new single-element selection and only that box moves.
 *
 * Pure helpers (`computeGroupDrag`, `clampBboxToPage`, `pixelDeltaToPdf`)
 * run in node tests; the controller is browser-only.
 *
 * Wire shape: each completed drag emits one `set-bbox` SPA op per moved
 * struct id (see `ir.js` typedef + `save-flow.js#opToWireOp`). The host
 * editor (`editor.js`) is responsible for pushing each op to the oplog
 * and bundling them under one undo-stack frame.
 *
 * Esc during a drag cancels and restores the original positions; no op
 * is emitted.
 *
 * Migrated to TypeScript 2026-06-24 as part of rule-#24 wave
 * (ts-unit-testability-seams-260623 Phase 2).
 *
 * @module drag-move
 */
import { cssEscape } from "./dom-utils.js";
import { GestureController } from "./gesture-controller.js";
const MIN_DRAG_PX = 3; // below which we treat as a click, not a drag.
/**
 * Compute the new bbox for each member of a multi-select drag, given a
 * source box, a pointer delta in PDF user-space, and the current bboxes
 * keyed by structId. The source box's anchor (top-left in PDF user-space
 * = bottom-left after our coord flip) translates by exactly delta;
 * every other member's anchor preserves its relative offset.
 *
 * Single-box drag: pass `selectionIds = [sourceId]` and `bboxesById`
 * containing only that id. The function still works.
 */
export function computeGroupDrag({ sourceId, selectionIds, bboxesById, delta }) {
    const out = new Map();
    if (!Array.isArray(selectionIds) || selectionIds.length === 0)
        return out;
    if (!bboxesById || typeof bboxesById.get !== "function")
        return out;
    const dx = delta.dx;
    const dy = delta.dy;
    for (const id of selectionIds) {
        const b = bboxesById.get(id);
        if (!b || b.length !== 4)
            continue;
        out.set(id, [b[0] + dx, b[1] + dy, b[2], b[3]]);
    }
    // Source must always be present in the output even if the caller
    // omitted it from selectionIds (defensive).
    if (sourceId && !out.has(sourceId)) {
        const b = bboxesById.get(sourceId);
        if (b && b.length === 4) {
            out.set(sourceId, [b[0] + dx, b[1] + dy, b[2], b[3]]);
        }
    }
    return out;
}
/**
 * Clamp a bbox to the page bounds so the user can't drag a box off-page.
 * The clamp preserves the bbox's width and height — only the origin
 * (x, y) shifts. Returns a fresh array.
 *
 * @param bbox - [x, y, w, h] in PDF user-space.
 * @param page - Page size in PDF user-space points.
 */
export function clampBboxToPage(bbox, page) {
    if (!Array.isArray(bbox) || bbox.length !== 4)
        return bbox;
    if (!page || !Number.isFinite(page.width) || !Number.isFinite(page.height))
        return bbox;
    const w = bbox[2];
    const h = bbox[3];
    const minX = 0;
    const maxX = Math.max(0, page.width - w);
    const minY = 0;
    const maxY = Math.max(0, page.height - h);
    const x = Math.min(maxX, Math.max(minX, bbox[0]));
    const y = Math.min(maxY, Math.max(minY, bbox[1]));
    return [x, y, w, h];
}
/**
 * Convert a CSS-pixel delta on the canvas to a PDF user-space delta.
 * The canvas y-axis points down; PDF user-space y-axis points up.
 *
 * @param pixelDelta - Canvas-px delta.
 * @param scale - CSS-px per PDF-point.
 * @returns PDF-user-space delta.
 */
export function pixelDeltaToPdf(pixelDelta, scale) {
    const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
    const dx = (pixelDelta && Number.isFinite(pixelDelta.dx)) ? pixelDelta.dx : 0;
    const dy = (pixelDelta && Number.isFinite(pixelDelta.dy)) ? pixelDelta.dy : 0;
    return { dx: dx / s, dy: -dy / s };
}
/**
 * Controller for drag-to-move overlay bounding boxes.
 *
 * @param hooks - Dependency injection for overlay container and callbacks.
 * @returns Handle with `destroy()` and `_isDraggingForTest()`.
 */
export function DragMoveController(hooks) {
    if (!hooks || !hooks.overlayContainer || typeof hooks.onCommit !== "function") {
        throw new Error("DragMoveController: overlayContainer + onCommit required");
    }
    const root = hooks.overlayContainer;
    const doc = root.ownerDocument ?? (typeof document !== "undefined" ? document : null);
    if (!doc)
        throw new Error("DragMoveController: no ownerDocument");
    /**
     * Drag-to-move gesture on existing .editor-overlay-bbox elements.
     *
     * Context shape: { origin:{x,y}, sourceId:string,
     *                  participants:Array<{structId, originalBbox, el,
     *                                      originalCssLeft, originalCssTop}> }
     *
     * Side effects:
     *   on engage:   adds .is-dragging to each participant element.
     *   on move:     live-translates each participant's CSS left/top.
     *   on commit:   removes .is-dragging; clamps each move to the page;
     *                fires hooks.onCommit with non-trivial moves.
     *   on cancel:   removes .is-dragging; restores each participant's CSS.
     */
    class DragGesture extends GestureController {
        onPointerDown(ev) {
            if (ev.button !== 0)
                return null; // left button only
            // Resize handles live INSIDE the bbox; resize-move.js claims those
            // pointerdowns. Yield so we don't drag the whole box during a resize.
            const evTarget = ev.target;
            if (evTarget?.closest?.(".editor-resize-handle"))
                return null;
            const target = evTarget?.closest?.(".editor-overlay-bbox");
            if (!target)
                return null; // click on empty area: insert-flow handles it.
            const sourceId = target.dataset?.structId;
            if (!sourceId)
                return null;
            // Promote source to a single-element selection if it's not currently
            // selected. The selection updates BEFORE we read it so the drag
            // includes the right participants.
            const sel = (typeof hooks.getSelected === "function") ? hooks.getSelected() : new Set();
            const inSelection = sel?.has?.(sourceId) ?? false;
            if (!inSelection) {
                hooks.onSelectIfUnselected?.(sourceId);
            }
            // Re-read selection (may have been updated by onSelectIfUnselected).
            const liveSel = (typeof hooks.getSelected === "function") ? hooks.getSelected() : new Set();
            const participantIds = (liveSel && liveSel.size >= 2 && liveSel.has(sourceId))
                ? Array.from(liveSel)
                : [sourceId];
            const bboxesById = (typeof hooks.getBboxesById === "function") ? hooks.getBboxesById() : new Map();
            const participants = [];
            for (const id of participantIds) {
                const el = root.querySelector(`.editor-overlay-bbox[data-struct-id="${cssEscape(id)}"]`);
                const bb = bboxesById.get(id);
                if (!el || !bb)
                    continue;
                participants.push({
                    structId: id,
                    originalBbox: bb.slice(),
                    el,
                    originalCssLeft: parseFloat(el.style.left) || 0,
                    originalCssTop: parseFloat(el.style.top) || 0,
                });
            }
            if (participants.length === 0)
                return null;
            const rect = root.getBoundingClientRect();
            // Visual cue: dragging class on every participant.
            for (const p of participants)
                p.el.classList.add("is-dragging");
            ev.preventDefault?.();
            return {
                origin: { x: ev.clientX - rect.left, y: ev.clientY - rect.top },
                sourceId,
                participants,
            };
        }
        onPointerMove(context, ev) {
            const rect = root.getBoundingClientRect();
            const cur = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
            const dxPx = cur.x - context.origin.x;
            const dyPx = cur.y - context.origin.y;
            // Live-translate participants in CSS-px for instant feedback. This
            // does NOT mutate the underlying bbox cache — that happens only on
            // pointer-up via onCommit.
            for (const p of context.participants) {
                p.el.style.left = `${p.originalCssLeft + dxPx}px`;
                p.el.style.top = `${p.originalCssTop + dyPx}px`;
            }
        }
        onCommit(context, ev) {
            const rect = root.getBoundingClientRect();
            const cur = ev ? { x: ev.clientX - rect.left, y: ev.clientY - rect.top } : context.origin;
            const dxPx = cur.x - context.origin.x;
            const dyPx = cur.y - context.origin.y;
            for (const p of context.participants)
                p.el.classList.remove("is-dragging");
            // Below threshold → treat as a click; restore positions and emit nothing.
            if (Math.abs(dxPx) < MIN_DRAG_PX && Math.abs(dyPx) < MIN_DRAG_PX) {
                for (const p of context.participants) {
                    p.el.style.left = `${p.originalCssLeft}px`;
                    p.el.style.top = `${p.originalCssTop}px`;
                }
                return;
            }
            const scale = (typeof hooks.getScale === "function") ? hooks.getScale() : 1;
            const pdfDelta = pixelDeltaToPdf({ dx: dxPx, dy: dyPx }, scale);
            const pageSize = (typeof hooks.getPageSize === "function") ? hooks.getPageSize() : null;
            // Build the per-participant move list. Each move clamps independently
            // so a wide selection doesn't all collapse if one member would clip
            // off the page edge.
            const bboxesById = new Map(context.participants.map((p) => [p.structId, p.originalBbox]));
            const newBboxes = computeGroupDrag({
                sourceId: context.sourceId,
                selectionIds: context.participants.map((p) => p.structId),
                bboxesById,
                delta: pdfDelta,
            });
            const moves = [];
            for (const p of context.participants) {
                const after = newBboxes.get(p.structId) ?? p.originalBbox;
                const clamped = pageSize ? clampBboxToPage(after, pageSize) : after;
                // Skip moves where the bbox didn't actually change (e.g. clamped
                // to the same edge it was already touching).
                const same = (clamped[0] === p.originalBbox[0] &&
                    clamped[1] === p.originalBbox[1] &&
                    clamped[2] === p.originalBbox[2] &&
                    clamped[3] === p.originalBbox[3]);
                if (same)
                    continue;
                moves.push({
                    structId: p.structId,
                    before: p.originalBbox.slice(),
                    after: clamped,
                });
            }
            // Commit (host pushes one set-bbox op per move + one undo frame).
            // If every box clamped to its original position the moves array is
            // empty and we still mark drag complete so the controller resets.
            if (moves.length > 0) {
                hooks.onCommit({ sourceId: context.sourceId, moves });
                // host-side throws bubble through the base GestureController,
                // which logs at warn — controller has no recovery path beyond
                // resetting drag state, which the base does automatically.
            }
        }
        onCancel(context) {
            // Restore CSS positions and drop the gesture without committing.
            for (const p of context.participants) {
                p.el.classList.remove("is-dragging");
                p.el.style.left = `${p.originalCssLeft}px`;
                p.el.style.top = `${p.originalCssTop}px`;
            }
        }
    }
    const gesture = new DragGesture({
        name: "drag-move",
        target: root,
        document: doc,
    });
    return {
        destroy() {
            gesture.destroy();
        },
        _isDraggingForTest() { return gesture._getContextForTest() !== null; },
    };
}
